import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ChatsCircle, PaperPlaneTilt } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuestMessagesPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    loadConversations();
  }, [token, navigate]);

  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/guest/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/messages/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        
        // Mark messages as read
        await fetch(`${API_URL}/api/messages/${bookingId}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        loadConversations(); // Refresh unread counts
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: selectedConversation.booking_id,
          content: newMessage
        })
      });
      
      if (response.ok) {
        setNewMessage('');
        loadMessages(selectedConversation.booking_id);
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv.booking_id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-400">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="text-3xl font-serif text-white mb-8">Messages</h1>
        
        {conversations.length === 0 ? (
          <div className="text-center py-16 bg-[#121212] border border-white/10">
            <ChatsCircle size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
            <h3 className="text-xl text-white mb-2">No messages yet</h3>
            <p className="text-zinc-500 mb-6">Messages with hosts will appear here after you make a booking</p>
            <Button
              onClick={() => navigate('/search')}
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
            >
              Explore Spaces
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="lg:col-span-1 bg-[#121212] border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Conversations</h3>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {conversations.map((conv) => (
                  <button
                    key={conv.booking_id}
                    onClick={() => selectConversation(conv)}
                    data-testid={`conversation-${conv.booking_id}`}
                    className={`w-full p-4 text-left border-b border-white/5 hover:bg-white/5 transition-colors ${
                      selectedConversation?.booking_id === conv.booking_id ? 'bg-[#D4AF37]/10 border-l-2 border-l-[#D4AF37]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-semibold text-white truncate">{conv.host_name}</p>
                      <div className="flex items-center gap-2">
                        {conv.unread_count > 0 && (
                          <span className="bg-[#D4AF37] text-black text-xs px-2 py-0.5 rounded-full font-semibold">
                            {conv.unread_count}
                          </span>
                        )}
                        {conv.last_message && (
                          <span className="text-xs text-zinc-600">
                            {formatDate(conv.last_message.created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-zinc-500 truncate">{conv.listing_title}</p>
                    {conv.booking_date && (
                      <p className="text-xs text-zinc-600 mt-1">
                        {new Date(conv.booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {conv.start_time && ` • ${conv.start_time}`}
                      </p>
                    )}
                    {conv.last_message && (
                      <p className="text-xs text-zinc-500 truncate mt-1">
                        {conv.last_message.sender_id === user?.id ? 'You: ' : ''}
                        {conv.last_message.content}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Messages Panel */}
            <div className="lg:col-span-2 bg-[#121212] border border-white/10 flex flex-col h-[600px]">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-white/10">
                    <h3 className="text-lg font-semibold text-white">{selectedConversation.host_name}</h3>
                    <p className="text-sm text-zinc-500">{selectedConversation.listing_title}</p>
                    {selectedConversation.booking_date && (
                      <p className="text-xs text-zinc-600 mt-1">
                        {new Date(selectedConversation.booking_date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        {selectedConversation.start_time && selectedConversation.end_time && (
                          <span> • {selectedConversation.start_time} - {selectedConversation.end_time}</span>
                        )}
                      </p>
                    )}
                    <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                      selectedConversation.booking_status === 'approved' ? 'bg-green-500/10 text-green-500' :
                      selectedConversation.booking_status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' :
                      selectedConversation.booking_status === 'completed' ? 'bg-blue-500/10 text-blue-500' :
                      'bg-zinc-500/10 text-zinc-500'
                    }`}>
                      {selectedConversation.booking_status?.charAt(0).toUpperCase() + selectedConversation.booking_status?.slice(1)}
                    </span>
                  </div>
                  
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-zinc-500">No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isOwnMessage = msg.sender_id === user?.id;
                        return (
                          <div 
                            key={msg.id} 
                            className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-xs font-semibold text-zinc-400">
                                {isOwnMessage ? 'You' : msg.sender_name}
                              </p>
                              <p className="text-xs text-zinc-600">
                                {new Date(msg.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className={`max-w-[80%] p-3 rounded ${
                              isOwnMessage 
                                ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' 
                                : 'bg-[#0A0A0A] border border-white/10'
                            }`}>
                              <p className="text-zinc-300">{msg.content}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Input */}
                  <div className="p-4 border-t border-white/10">
                    <div className="flex gap-3">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="bg-transparent border-white/20 text-white flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendMessage()}
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={sending || !newMessage.trim()}
                        className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                        data-testid="send-message-btn"
                      >
                        <PaperPlaneTilt size={20} weight="fill" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <ChatsCircle size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
                    <p className="text-zinc-500">Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
